import { useAemConfig } from '@as-react/common-components';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeGetHasConsentOnMarketingCookies } from '../redux/cookieConsent/selectors/CookieConsentSelector';
import { initBloomreach, identifyBloomreach } from '../util/bloomreach';
import { useOAuth } from './useOAuth';
/**
 * Hook that manages the Bloomreach Engagement SDK lifecycle.
 *
 * Responsibilities:
 * - Calls brweb.start() after marketing cookie consent is granted
 * - Identifies the customer (brweb.identify) on login or returning authenticated session
 *
 * Bloomreach is attached to the generic tracking emitter at module-load time via
 * `util/tracking`, so this hook no longer needs to subscribe explicitly.
 *
 * Anonymize on logout is handled in useOAuth.logout() before the page redirect.
 *
 * Mount this once at app root (Bootstrap).
 * Use emitter.emit() from components to send vendor-agnostic events.
 */
export const useBloomreachTracking = (debug = true) => {
    const { isShopLanding, bloomreachConfig: config } = useAemConfig();
    const getHasMarketingConsent = useMemo(makeGetHasConsentOnMarketingCookies, []);
    const hasMarketingConsent = useSelector(getHasMarketingConsent);
    // SDK is only active when marketing consent is granted and not on shop landing pages
    const isActive = hasMarketingConsent && !isShopLanding;
    const { isLoggedIn, userInfo } = useOAuth();
    const customerUuid = userInfo === null || userInfo === void 0 ? void 0 : userInfo.sub;
    // Initialize the SDK and call brweb.start(). Subscriber attachment happens at
    // module load via `util/tracking` in each emitting bundle.
    useEffect(() => {
        if (!config || !isActive) {
            return;
        }
        initBloomreach(config, debug);
    }, [config === null || config === void 0 ? void 0 : config.isEnabled, isActive, debug]);
    // Identify the customer on login or when returning with an active session
    useEffect(() => {
        if (!(config === null || config === void 0 ? void 0 : config.isEnabled) || !isActive || !isLoggedIn || !customerUuid) {
            return;
        }
        identifyBloomreach({ customer_uuid: customerUuid });
    }, [config === null || config === void 0 ? void 0 : config.isEnabled, isActive, isLoggedIn, customerUuid]);
};
